import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { formatStamp } from '../utils/datetime';
import '../styles/ReportPanel.css';

/**
 * Instant report — one screen that renders all twelve.
 *
 * The server sends a typed envelope (`reports_engine`): KPI tiles, then a list of typed
 * blocks. Nothing here knows what report it is drawing, which is the point — a thirteenth
 * report is a builder on the backend and no frontend work at all.
 *
 * Three things on this screen are load-bearing and easy to remove by accident:
 *
 *  1. **Freshness sits ABOVE the numbers, not under them.** Every figure here renders a
 *     snapshot this report did not build (Critical Note #25). A reader has to know how old
 *     the evidence is *before* reading the conclusion drawn from it, and has to be told when
 *     a contributing panel has never been built at all — an unbuilt panel and a compliant
 *     one are indistinguishable in every total.
 *  2. **`null` is not zero.** A cell whose value the server could not determine prints "—"
 *     with the reason on hover. "Nothing is overdue" and "we cannot tell you" are opposite
 *     claims (Critical Note #45), and a report that renders the second as the first is
 *     exactly the clean, plausible, wrong screen this app keeps paying for.
 *  3. **`problems[]` is never removed.** It is what the report could not answer. It collapses
 *     behind a warning badge that is always visible and always counts them, so the reader is
 *     told a caveat exists before reading the figures — but an unread caveat must not push the
 *     report itself below the fold. The badge is the caveat; the text is one click away.
 *
 * Tones (`ok open miss bad mute s1..s5`) resolve to CSS variables here and are never hex on
 * the server — so a theme change is one stylesheet, not twelve builders.
 */

const AUTO_REFRESH_MS = 5 * 60 * 1000;

/**
 * Every figure that has rows behind it carries a `drill` key, and the API serves one drawer at
 * a time. The context is passed rather than threaded through every block's props: a bar, a
 * donut slice, a heat-map cell and a table row all open the same drawer, and each of them is
 * three components deep.
 */
const DrillContext = React.createContext(null);

function useDrill(key) {
  const open = React.useContext(DrillContext);
  if (!key || !open) return {};
  return {
    role: 'button',
    tabIndex: 0,
    'data-drill': 'yes',
    title: 'Open the rows behind this figure',
    onClick: (e) => { e.stopPropagation(); open(key); },
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); open(key); }
    },
  };
}

/** Bold spans written as **this** by the server's feed lines. Plain text otherwise. */
function RichText({ text }) {
  const parts = String(text || '').split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((p, i) => (p.startsWith('**') && p.endsWith('**')
        ? <b key={i}>{p.slice(2, -2)}</b>
        : <React.Fragment key={i}>{p}</React.Fragment>))}
    </>
  );
}

/* ------------------------------------------------------------------ blocks */

function Bars({ block }) {
  // One scale for every row, so two bars of the same length mean the same thing. Taken from
  // the widest row (or an explicit max, which is what a percentage block sends) — scaling
  // each row to its own total would draw every row full and encode nothing.
  const max = block.max
    || Math.max(1, ...block.rows.map((r) => (r.segments || []).reduce((a, s) => a + (s.v || 0), 0)));
  return (
    <figure className="rp-block">
      {block.title && <figcaption className="rp-block-title">{block.title}</figcaption>}
      <div className="rp-bars">
        {block.rows.map((r, i) => <BarRow key={i} r={r} max={max} />)}
      </div>
      {/* A capped chart that says nothing about the cap reads as the whole account. This line
          is how the rows that were not drawn stay reachable — and how the reader learns they
          exist at all. */}
      {block.more && <BarsMore more={block.more} />}
      <Legend items={block.legend} />
    </figure>
  );
}

function BarsMore({ more }) {
  const drill = useDrill(more.drill);
  return (
    <button type="button" className="rp-bars-more rp-clickable" {...drill}>
      {more.label} →
    </button>
  );
}

/** The one figure a report exists to give, at the size that says so. */
function Hero({ block }) {
  const unit = block.unit ? <span className="rp-hero-unit">{block.unit}</span> : null;
  return (
    <div className="rp-block rp-hero-block">
      <div className={`rp-hero rp-ink-${block.tone || ''}`}>
        {block.unit_before && unit}
        <b>{block.v}</b>
        {!block.unit_before && unit}
      </div>
      {/* Newlines in `sub` are the payload's own line breaks — it is two facts, not a
          paragraph, and wrapping them by width would break them in the wrong place. */}
      {block.sub && (
        <p className="rp-hero-sub">
          {String(block.sub).split('\n').map((line, i) => (
            <span key={i}>{line}</span>
          ))}
        </p>
      )}
    </div>
  );
}

/** A tile row as a block, so tiles can sit in a column beside a chart. */
function Tiles({ block }) {
  // `per_row`, not `cols` — a table's cols are definitions and a split's cols are blocks.
  const style = block.per_row
    ? { gridTemplateColumns: `repeat(${block.per_row}, minmax(0, 1fr))` }
    : undefined;
  return (
    // NOT `rp-block` — `.rp-kpis` already draws its own frame, and the two together give a
    // border inside a border. The block spacing is in `.rp-kpis-inline`.
    <div className="rp-kpis rp-kpis-inline" style={style}>
      {(block.items || []).map((k, i) => (
        <div className={`rp-kpi rp-ink-${k.tone || ''}`} key={i}>
          <span className="rp-kpi-k">{k.k}</span>
          <span className="rp-kpi-v">{k.v}</span>
          {k.d && <span className="rp-kpi-d">{k.d}</span>}
        </div>
      ))}
    </div>
  );
}

/**
 * Two columns of blocks.
 *
 * Layout is part of what a report means: a doughnut beside the table that breaks it down is
 * one statement; the same two stacked with a screen of white between them is two. The columns
 * stack on a narrow screen, which is the only responsive behaviour this can honestly have.
 */
function Split({ block }) {
  return (
    <div className={`rp-split${block.even ? ' rp-split-even' : ''}`}>
      {(block.cols || []).map((col, i) => (
        <div className="rp-split-col" key={i}>
          {col.map((child, j) => {
            const C = BLOCKS[child.type];
            return C ? <C key={j} block={child} /> : null;
          })}
        </div>
      ))}
    </div>
  );
}

function BarRow({ r, max }) {
  const drill = useDrill(r.drill);
  return (
    <div className={`rp-bar-row${r.drill ? ' rp-clickable' : ''}`} {...drill}>
      <span className="rp-bar-label" title={r.label}>
        {r.label}
        {r.note && <em>{r.note}</em>}
      </span>
      <span className="rp-bar-track">
        {(r.segments || []).map((s, j) => (
          <i key={j} className={`rp-t-${s.tone}`} style={{ width: `${((s.v || 0) / max) * 100}%` }} />
        ))}
      </span>
      <span className="rp-bar-value">{r.value}</span>
    </div>
  );
}

function Legend({ items }) {
  if (!items || !items.length) return null;
  return (
    <div className="rp-legend">
      {items.map((l, i) => (
        <span key={i}><i className={`rp-sw rp-t-${l.tone}`} />{l.label}</span>
      ))}
    </div>
  );
}

function Line({ block }) {
  const pts = block.points || [];
  if (pts.length < 2) return null;
  const W = 640; const H = 190; const L = 38; const R = 14; const T = 14; const B = 26;
  const max = Math.max(100, ...pts.map((p) => p.y));
  const x = (i) => L + (i * (W - L - R)) / (pts.length - 1);
  const y = (v) => T + (H - T - B) * (1 - v / max);
  const line = pts.map((p, i) => `${x(i)},${y(p.y)}`).join(' ');
  const area = `${L},${y(0)} ${line} ${x(pts.length - 1)},${y(0)}`;
  const ticks = [0, 25, 50, 75, 100].filter((t) => t <= max);
  const last = pts[pts.length - 1];
  return (
    <figure className="rp-block">
      {block.title && <figcaption className="rp-block-title">{block.title}</figcaption>}
      <svg viewBox={`0 0 ${W} ${H}`} className="rp-svg" role="img"
           aria-label={`${block.title}: ${pts.map((p) => `${p.x} ${p.y}${block.unit || ''}`).join(', ')}`}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={L} y1={y(t)} x2={W - R} y2={y(t)} className="rp-grid" />
            <text x={L - 6} y={y(t) + 3} textAnchor="end" className="rp-axis">{t}{block.unit}</text>
          </g>
        ))}
        <polygon points={area} className={`rp-fill rp-f-${block.tone || 's1'}`} />
        <polyline points={line} fill="none" className={`rp-line rp-s-${block.tone || 's1'}`} />
        <circle cx={x(pts.length - 1)} cy={y(last.y)} r="4.5" className={`rp-dot rp-s-${block.tone || 's1'}`} />
        <text x={x(pts.length - 1) - 6} y={y(last.y) - 9} textAnchor="end" className="rp-endlabel">
          {last.y}{block.unit}
        </text>
        {pts.map((p, i) => (
          (i === 0 || i === pts.length - 1 || i === Math.floor(pts.length / 2)) && (
            <text key={i} x={x(i)} y={H - 6} textAnchor={i === 0 ? 'start' : i === pts.length - 1 ? 'end' : 'middle'}
                  className="rp-axis">{p.x}</text>
          )
        ))}
        {/* A hit target per point, wider than the mark, carrying the row's own denominator. */}
        {pts.map((p, i) => (
          <rect key={`h${i}`} x={x(i) - 12} y={T} width="24" height={H - T - B} fill="transparent">
            <title>{`${p.x} · ${p.y}${block.unit || ''}${p.label ? ` · ${p.label}` : ''}`}</title>
          </rect>
        ))}
      </svg>
    </figure>
  );
}

function Columns({ block }) {
  const cols = block.cols || [];
  const max = Math.max(1, ...cols.map((c) => c.value || 0));
  return (
    <figure className="rp-block">
      {block.title && <figcaption className="rp-block-title">{block.title}</figcaption>}
      <div className="rp-cols">
        {cols.map((c, i) => <Column key={i} c={c} max={max} block={block} />)}
      </div>
      <Legend items={block.legend} />
    </figure>
  );
}

function Column({ c, max, block }) {
  const drill = useDrill(c.drill);
  return (
    <div className={`rp-col${c.drill ? ' rp-clickable' : ''}`} {...drill}>
      <div className="rp-col-stack" title={`${c.label}: ${block.unit === '$' ? '$' : ''}${c.value}`}>
        {(c.parts && c.parts.length
          ? c.parts
          : [{ v: c.value, tone: block.tone || 's1' }]
        ).map((p, j) => (
          <i key={j} className={`rp-t-${p.tone}`} style={{ height: `${((p.v || 0) / max) * 100}%` }} />
        ))}
      </div>
      <span className="rp-col-label">{c.label}</span>
    </div>
  );
}

function Donut({ block }) {
  const slices = (block.slices || []).filter((s) => s.v > 0);
  const total = slices.reduce((a, s) => a + s.v, 0) || 1;
  const R = 66; const C = 2 * Math.PI * R;
  let offset = 0;
  return (
    <figure className="rp-block rp-donut-block">
      {block.title && <figcaption className="rp-block-title">{block.title}</figcaption>}
      <div className="rp-donut-wrap">
        <svg viewBox="0 0 180 180" className="rp-donut" role="img"
             aria-label={slices.map((s) => `${s.label} ${s.v}`).join(', ')}>
          <circle cx="90" cy="90" r={R} className="rp-donut-track" />
          {slices.map((s, i) => {
            const len = (s.v / total) * C;
            const el = (
              <circle key={i} cx="90" cy="90" r={R} className={`rp-donut-arc rp-t-${s.tone}`}
                      strokeDasharray={`${len} ${C}`} strokeDashoffset={-offset}
                      transform="rotate(-90 90 90)">
                <title>{`${s.label}: ${s.v}`}</title>
              </circle>
            );
            offset += len;
            return el;
          })}
        </svg>
        {block.center && (
          <div className="rp-donut-center">
            <b>{block.center.v}<span>{block.center.unit}</span></b>
            <em>{block.center.label}</em>
          </div>
        )}
      </div>
      {/* The slices are also the legend's rows, so the legend is where they are clicked: an
          arc is a poor hit target and the label is the thing a reader is already pointing at. */}
      <div className="rp-legend">
        {slices.map((s, i) => <LegendDrill key={i} s={s} />)}
      </div>
    </figure>
  );
}

function LegendDrill({ s }) {
  const drill = useDrill(s.drill);
  return (
    <span className={s.drill ? 'rp-clickable' : ''} {...drill}>
      <i className={`rp-sw rp-t-${s.tone}`} />{s.label} {s.v}
    </span>
  );
}

/** A value the server could not determine. Never rendered as 0 — see the header note. */
function Unknown({ why }) {
  return <span className="rp-unknown" title={why || 'This report could not determine this value.'}>—</span>;
}

function Cell({ col, row }) {
  const v = row[col.k];
  switch (col.kind) {
    case 'pill':
      return <span className={`rp-pill rp-t-${row.tone || 'mute'}`}>{v}</span>;
    case 'mono':
      return <span className="rp-mono">{v ?? '—'}</span>;
    case 'num':
      return v == null ? <Unknown /> : <span className="rp-mono">{v}</span>;
    case 'money':
      return <span className="rp-mono">${Number(v || 0).toFixed(Number(v) < 0.01 ? 4 : 2)}</span>;
    case 'pct':
      return v == null
        ? <Unknown why="This panel has no snapshot, so it has no percentage." />
        : <span className="rp-mono">{v}%</span>;
    case 'days':
      return v == null
        ? <Unknown why="The due date on this task could not be read, so its age is unknown — it is not a claim that the task is on time." />
        : <span className={`rp-mono rp-ink-${row.tone}`}>{v}</span>;
    case 'unknownable':
      return v == null
        ? <Unknown why="Nobody can be shown as 'not started' until the audience for this quiz is known." />
        : <span className="rp-mono">{v}</span>;
    case 'minibar': {
      const segs = v || [];
      const total = segs.reduce((a, s) => a + (s.v || 0), 0);
      // A bar with nothing behind it is never drawn empty — an empty track reads as "all
      // missing", which is a claim, not an absence. The column says WHY it has no figure,
      // because the same cell kind serves a quiz with no known audience and a panel that
      // has never been built, and those are different findings.
      if (!total) {
        return <Unknown why={col.unknown
          || 'This row has no counted obligations, so it has no standing to draw.'} />;
      }
      return (
        <span className="rp-minibar">
          {segs.map((s, i) => (
            <i key={i} className={`rp-t-${s.tone}`} style={{ width: `${(s.v / total) * 100}%` }} />
          ))}
        </span>
      );
    }
    default:
      return (
        <span>
          <span className="rp-cell-main">{v ?? '—'}</span>
          {col.sub && row[col.sub] ? <em className="rp-cell-sub">{row[col.sub]}</em> : null}
        </span>
      );
  }
}

function Table({ block }) {
  if (block.kind === 'heatmap') return <Heatmap block={block} />;
  return (
    <figure className="rp-block">
      {block.title && <figcaption className="rp-block-title">{block.title}</figcaption>}
      <div className="rp-tbl-wrap">
        <table className="rp-tbl">
          <thead>
            <tr>{block.cols.map((c) => (
              <th key={c.k} className={c.align === 'r' ? 'r' : ''}>{c.label}</th>
            ))}</tr>
          </thead>
          <tbody>
            {block.rows.map((row, i) => <Row key={i} row={row} block={block} />)}
          </tbody>
        </table>
      </div>
    </figure>
  );
}

function Row({ row, block }) {
  const drill = useDrill(row.drill);
  const cls = [block.stripe ? `rp-stripe rp-b-${row.tone || 'mute'}` : '',
               row.drill ? 'rp-clickable' : ''].filter(Boolean).join(' ');
  return (
    <tr className={cls} {...drill}>
      {block.cols.map((c) => (
        <td key={c.k} className={c.align === 'r' ? 'r' : ''}>
          <Cell col={c} row={row} />
        </td>
      ))}
    </tr>
  );
}

/** Panel × level. A systemic hole reads as a column, a single bad panel as a row. */
function Heatmap({ block }) {
  const levels = block.levels || [];
  return (
    <figure className="rp-block">
      {block.title && <figcaption className="rp-block-title">{block.title}</figcaption>}
      <div className="rp-tbl-wrap">
        <table className="rp-tbl">
          <thead>
            <tr>
              <th>Panel</th>
              {levels.map((l) => <th key={l} className="r">{l}</th>)}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, i) => {
              const byLevel = Object.fromEntries((row.cells || []).map((c) => [c.level, c]));
              return (
                <tr key={i}>
                  <td><span className="rp-cell-main">{row.panel}</span></td>
                  {levels.map((l) => <HeatCell key={l} c={byLevel[l]} />)}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="rp-legend">
        <span><i className="rp-sw rp-t-mute" />under 5%</span>
        <span><i className="rp-sw rp-t-open" />5–9%</span>
        <span><i className="rp-sw rp-t-miss" />10–24%</span>
        <span><i className="rp-sw rp-t-bad" />25% and over</span>
      </div>
    </figure>
  );
}

function HeatCell({ c }) {
  const drill = useDrill(c && c.drill);
  if (!c || c.v == null) {
    return <td className="r"><Unknown why="This panel has no level of that name." /></td>;
  }
  return (
    <td className="r">
      <span className={`rp-pill rp-t-${c.tone}${c.drill ? ' rp-clickable' : ''}`}
            title={c.drill ? `${c.note} — open the items` : c.note} {...drill}>
        {c.v}%
      </span>
    </td>
  );
}

function Alerts({ block }) {
  return (
    <div className="rp-block rp-alerts">
      {(block.items || []).map((a, i) => (
        <div className={`rp-alert rp-b-${a.tone}`} key={i}>
          <span className={`rp-alert-mark rp-t-${a.tone}`} />
          <div>
            <h4>{a.title}</h4>
            {a.body && <p>{a.body}</p>}
          </div>
        </div>
      ))}
    </div>
  );
}

function Feed({ block }) {
  return (
    <figure className="rp-block">
      {block.title && <figcaption className="rp-block-title">{block.title}</figcaption>}
      <div className="rp-feed">
        {(block.items || []).map((it, i) => (
          <div className="rp-feed-item" key={i}>
            <span className="rp-feed-when">{it.when}</span>
            <span className={`rp-feed-dot rp-t-${it.tone || 'mute'}`} />
            <span className="rp-feed-text"><RichText text={it.text} /></span>
          </div>
        ))}
      </div>
    </figure>
  );
}

function Funnel({ block }) {
  return (
    <figure className="rp-block">
      {block.title && <figcaption className="rp-block-title">{block.title}</figcaption>}
      <div className="rp-funnel">
        {(block.stages || []).map((s, i) => (
          <div className="rp-funnel-row" key={i}>
            <span className="rp-funnel-bar" style={{ width: `${s.width}%` }}>
              <b>{s.label}</b>
              <span>{s.value}</span>
            </span>
            <em>{s.note}</em>
          </div>
        ))}
      </div>
    </figure>
  );
}

function Empty({ block }) {
  return (
    <div className="rp-block rp-empty">
      <p>{block.message}</p>
      {block.hint && <span>{block.hint}</span>}
    </div>
  );
}

/**
 * The rows behind one figure.
 *
 * It is a panel beside the report rather than a route of its own, so the figure that was
 * clicked stays on screen next to the rows explaining it — the two are only meaningful
 * together, and a reader who has to navigate away loses the number they were asking about.
 */
function DrillDrawer({ state, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const d = state.data;
  return (
    <>
      <div className="rp-drawer-backdrop" onClick={onClose} />
      <aside className="rp-drawer" role="dialog" aria-modal="true"
             aria-label={d ? d.title : 'Details'}>
        <div className="rp-drawer-bar">
          <h3>{d ? d.title : 'Details'}</h3>
          <button className="rp-drawer-x" onClick={onClose} aria-label="Close">×</button>
        </div>

        {state.loading && <div className="rp-loading">Reading the rows…</div>}
        {state.error && <div className="rp-error">{state.error}</div>}

        {d && (
          <div className="rp-drawer-body">
            {d.note && <p className="rp-drawer-note">{d.note}</p>}
            <div className="rp-drawer-count">
              {d.total} row{d.total === 1 ? '' : 's'}
              {/* A truncated list that looks complete is the same failure as a wrong total. */}
              {d.capped && <b> · showing the first {d.rows.length}</b>}
            </div>
            {d.rows.length === 0 ? (
              <div className="rp-empty"><p>Nothing is recorded behind this figure.</p></div>
            ) : (
              <div className="rp-tbl-wrap">
                <table className="rp-tbl">
                  <thead>
                    <tr>{d.cols.map((c) => (
                      <th key={c.k} className={c.align === 'r' ? 'r' : ''}>{c.label}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {d.rows.map((row, i) => (
                      <tr key={i}>
                        {d.cols.map((c) => (
                          <td key={c.k} className={c.align === 'r' ? 'r' : ''}>
                            {/* The row's url belongs to whichever column the payload marks
                                `link` — the first column only as a fallback, since that is
                                the item itself in the drawers that name no other. */}
                            {c.k === (d.cols.find((x) => x.link) || d.cols[0]).k && row.url
                              ? <a href={row.url} target="_blank" rel="noreferrer" className="rp-link">
                                  <Cell col={c} row={row} />
                                </a>
                              : <Cell col={c} row={row} />}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </aside>
    </>
  );
}

/**
 * What the report could not answer, collapsed behind its own count.
 *
 * The badge is never hidden and never says just "⚠" — it carries the number, because "there is
 * something you have not read" is the part that cannot wait for a click. What it collapses is
 * the paragraph, not the fact that there is one.
 */
function Problems({ items }) {
  const [open, setOpen] = useState(false);
  const list = items || [];
  if (!list.length) return null;
  return (
    <div className="rp-problems">
      <button type="button" className={`rp-problems-toggle${open ? ' rp-open' : ''}`}
              aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <span className="rp-problems-mark">⚠</span>
        {list.length} caveat{list.length === 1 ? '' : 's'} on this report
        <span className="rp-problems-chev">{open ? '▴' : '▾'}</span>
      </button>
      {open && list.map((p, i) => (
        <div className="rp-problem" key={i}>{p}</div>
      ))}
    </div>
  );
}

const BLOCKS = {
  bars: Bars, line: Line, columns: Columns, donut: Donut,
  table: Table, alerts: Alerts, feed: Feed, funnel: Funnel, empty: Empty,
  // Layout blocks — they carry no data of their own, only where the data blocks go.
  hero: Hero, tiles: Tiles, split: Split,
};

/* ------------------------------------------------------------------ page */

function ReportPanel({ module = 'trace' }) {
  const { setupId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const reqRef = useRef(0);
  // { key, loading, error, data } — the one open drawer, or null.
  const [drill, setDrill] = useState(null);
  const drillRef = useRef(0);

  const openDrill = useCallback(async (key) => {
    const token = ++drillRef.current;
    setDrill({ key, loading: true, error: '', data: null });
    try {
      const r = await fetch(`/api/reports/${setupId}/drill?key=${encodeURIComponent(key)}`,
        { credentials: 'include' });
      const body = await r.json().catch(() => ({}));
      if (drillRef.current !== token) return;      // a later click already won
      if (!r.ok) throw new Error(body.detail || `Could not read those rows (${r.status})`);
      setDrill({ key, loading: false, error: '', data: body });
    } catch (err) {
      if (drillRef.current === token) setDrill({ key, loading: false, error: err.message, data: null });
    }
  }, [setupId]);

  const load = useCallback(async () => {
    const token = ++reqRef.current;
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`/api/reports/${setupId}`, { credentials: 'include' });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.detail || `Could not read this report (${r.status})`);
      if (reqRef.current === token) setData(body);
    } catch (err) {
      if (reqRef.current === token) setError(err.message);
    } finally {
      if (reqRef.current === token) setLoading(false);
    }
  }, [setupId]);

  useEffect(() => { load(); }, [load]);

  // A report is a grouping of other panels' snapshots, and one of those can be rebuilt while
  // this page is open. Re-read on a slow timer so a left-open dashboard is not quietly stale
  // — never on a fast one: this costs a snapshot parse, and it can never trigger a build.
  useEffect(() => {
    const t = setInterval(load, AUTO_REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  const home = module === 'training' ? '/training' : '/trace/dashboard';
  const sources = data?.sources || [];
  const unbuilt = sources.filter((s) => !s.built);

  return (
    <div className="rp-page">
      <div className="rp-head">
        <button className="rp-back" onClick={() => navigate(home)}>← Back</button>
        <div className="rp-head-text">
          <h1>{data?.name || 'Report'}</h1>
          <p className="rp-sub">{data?.blurb}</p>
        </div>
        <button className="rp-refresh" onClick={load} disabled={loading}>
          {loading ? 'Reading…' : '↻ Re-read'}
        </button>
      </div>

      {error && <div className="rp-error">{error}</div>}

      {/* Freshness ABOVE the figures. Every number below renders a snapshot this report did
          not build, and a reader has to know how old it is before reading it. */}
      {data && (
        <div className={`rp-freshness${unbuilt.length ? ' rp-freshness-warn' : ''}`}>
          {sources.length > 0 ? (
            <>
              <span>
                Read from <b>{sources.filter((s) => s.built).length}</b> of <b>{sources.length}</b>
                {sources.length === 1 ? ' panel' : ' panels'}
              </span>
              <span className="rp-sep">·</span>
              <span title="A figure merged from several snapshots is only as current as the least current evidence in it, so that is the date it carries.">
                oldest snapshot {formatStamp(data.built_at)}
              </span>
            </>
          ) : (
            <span>Read live from this account's own records — there is no snapshot behind this one.</span>
          )}
          {unbuilt.length > 0 && (
            <span className="rp-fresh-warn">
              ⚠ Never built, so nothing they cover is counted here:{' '}
              {unbuilt.map((s) => s.name).join(', ')}
            </span>
          )}
        </div>
      )}

      <Problems items={data?.problems} />

      {loading && !data ? (
        <div className="rp-loading">Reading the snapshots…</div>
      ) : data ? (
        <>
          {data.kpis?.length > 0 && (
            <div className="rp-kpis">
              {data.kpis.map((k, i) => (
                <div className={`rp-kpi rp-ink-${k.tone || ''}`} key={i}>
                  <span className="rp-kpi-k">{k.k}</span>
                  <span className="rp-kpi-v">{k.v}</span>
                  {k.d && <span className="rp-kpi-d">{k.d}</span>}
                </div>
              ))}
            </div>
          )}

          <DrillContext.Provider value={openDrill}>
            {(data.blocks || []).map((block, i) => {
              const C = BLOCKS[block.type];
              return C ? <C key={i} block={block} /> : null;
            })}
          </DrillContext.Provider>

          {/* What this report counted, in one sentence. A figure whose basis is not stated
              can be read three ways, and two of them are wrong. */}
          {data.basis && <p className="rp-basis"><b>What this counts</b> {data.basis}</p>}
        </>
      ) : null}

      {drill && <DrillDrawer state={drill} onClose={() => setDrill(null)} />}
    </div>
  );
}

export default ReportPanel;
